import React, { useEffect } from "react";
import { useLocation, useNavigate, Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  AdminRoutes,
  MPRoutes,
  AuthenticationRoutes,
  TrackingRoutes,
  DriverBehaviorRoutes,
  UnAuthenticatedRoutes,
} from "./containers/routes";
import { useCreateAuthMutation } from "./containers/Authentication/service";
import { FMDPStorage } from "./shared/helper";
import { useQuery } from "./hooks";
import { toggleAuthModal } from "./reduxStore/features";
import { FMDPWebSocket } from "./containers/Shared/WebSocket";
import { AuthError } from "./containers/Shared";
import { setWebSocket } from "./reduxStore/features";
import { PrintPreviewPage } from "./containers/Tracking/Reports/PrintPreviewPage";
import { FleetPerformancePreviewPage } from "./containers/Tracking/Reports/FleetPerformance/FleetPerformancePreviewPage";

let firstLocation = "";
let queryParams = "";

// Add all your unauthenticated routes here to
let UN_AUTHETICATED_ROUTES = ["documentation", "clients", "logged_out"];

// Preview routes that need auth but don't depend on RTK Query mutation cache.
// These are opened via window.open() from the app, so sessionStorage (token) is
// NOT available in the new tab. However, localStorage data (current-user, selected-app)
// persists and is sufficient to verify the user was previously authenticated.
const PREVIEW_PATHS = [
  "/tracking/reports/print-preview",
  "/tracking/reports/fleet-performance-preview",
];

const isPreviewRoute = (pathname) => {
  return PREVIEW_PATHS.some((p) => pathname.startsWith(p));
};

/**
 * Renders preview pages (print-preview, fleet-performance-preview) with a
 * lightweight auth gate that only checks localStorage, not RTK Query cache.
 * This avoids the redirect-to-login issue when opening previews in a new tab.
 */
const PreviewRoutes = () => {
  const currentUser = FMDPStorage.get("current-user");

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      <Route path="/tracking/reports/print-preview" element={<PrintPreviewPage />} />
      <Route path="/tracking/reports/fleet-performance-preview" element={<FleetPerformancePreviewPage />} />
    </Routes>
  );
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showAuthModal = useSelector((state) => state.common.showAuthModal);

  const query = useQuery();

  const userApp = FMDPStorage.get("selected-app");
  let paths = location.pathname.split("/");
  let subRoute = paths.length > 1 ? paths[1] : "";

  // Cache the location path
  useEffect(() => {
    firstLocation = location.pathname;

    // Filter out query Params
    for (let entry of query.entries()) {
      if (!queryParams) {
        queryParams = `${entry[0]}=${entry[1]}`;
      } else {
        queryParams = `${queryParams}&${entry[0]}=${entry[1]}`;
      }
    }
  }, []);

  const [, { data: userData }] = useCreateAuthMutation({
    fixedCacheKey: "shared-sso-data",
  });

  useEffect(() => {
    if (userData) {
      const ws = new FMDPWebSocket(userData.id);

      ws.subscribe("WebNotificationChannel", (response) => {
        if (response) {
          dispatch(setWebSocket(response));
        }
      });

      ws.subscribe("ForceLogoutNotificationChannel", (response) => {
        if (response.message === "force_logout") {
          dispatch(toggleAuthModal());
        }
      });
    }
  }, [userData]);
  // This effect will help to keep route persistence if page is force re-loaded
  // firstLocation will only change if application get loaded for first time(or when hard refresh)
  useEffect(() => {
    if (userData && userApp) {
      let redirectTo = firstLocation;
      if (queryParams) {
        redirectTo = `${firstLocation}?${queryParams}`;
      }
      navigate(redirectTo);
    }
  }, [userData]);

  return (
    <div className="App">
      {isPreviewRoute(location.pathname) ? (
        <PreviewRoutes />
      ) : !UN_AUTHETICATED_ROUTES.includes(subRoute) ? (
        <>
          {userData && userApp === "SuperAdmin" && <AdminRoutes />}
          {userData && userApp === "ManagementPortal" && <MPRoutes />}
          {userData && userApp === "Tracking" && <TrackingRoutes />}
          {userData && userApp === "EcoDriveDriverScore" && <DriverBehaviorRoutes />}
          {(!userData || !userApp) && <AuthenticationRoutes />}
          {showAuthModal && <AuthError />}
        </>
      ) : (
        <UnAuthenticatedRoutes />
      )}
    </div>
  );
}

export default App;
